import { NextSeo } from 'next-seo'
import { useRouter } from 'next/router'

import { getBaseUrl } from '@/lib/contentful/utils'

import { getSeoLocale } from '@/utils/locales/getSeoLocale'
import { useUrl } from '@/utils/url/useUrl'

import { useEntry } from '../Person.hooks'

type Props = {
	children: React.ReactNode
}

export function PersonMeta({ children }: Props) {
	const entry = useEntry()
	const { locale = 'en-US' } = useRouter()
	const url = useUrl({ locale })

	if ('error' in entry) return <>{children}</>

	const fullName = entry?.data?.fullName

	const fallbackTitle = `Posts from ${fullName} on the BigCommerce Blog`
	const fallbackDescription = `Great blog posts about ecommerce and growth from ${fullName} on the BigCommerce blog`

	return (
		<>
			<NextSeo
				title={entry.data.seoMetadata?.title ?? fallbackTitle}
				description={entry.data.seoMetadata?.description ?? fallbackDescription}
				canonical={
					entry.data.seoMetadata?.canonicalUrl ||
					`${url?.protocol}//${url?.host}/blog/author/${entry.data.slug}/`
				}
				languageAlternates={[
					{ hrefLang: 'en-au', href: `${getBaseUrl('en-AU')}/blog/author/${entry.data.slug}/` },
					{ hrefLang: 'en-gb', href: `${getBaseUrl('en-GB')}/blog/author/${entry.data.slug}/` },
					{ hrefLang: 'en-us', href: `${getBaseUrl('en-US')}/blog/author/${entry.data.slug}/` },
					{ hrefLang: 'x-default', href: `${getBaseUrl()}/blog/author/${entry.data.slug}/` }
				]}
				openGraph={{
					title: entry.data.seoMetadata?.title ?? fallbackTitle,
					description: entry.data.seoMetadata?.description ?? fallbackDescription,
					url: `${url?.protocol}//${url?.host}/blog/author/${entry.data.slug}/`,
					images: entry.data.seoMetadata?.image?.url
						? [
								{
									url: entry.data.seoMetadata.image.url,
									width: entry.data.seoMetadata.image.width,
									height: entry.data.seoMetadata.image.height,
									alt: 'Bigcommerce logo',
									type: 'image/jpeg'
								}
						  ]
						: undefined,
					locale: getSeoLocale(locale)
				}}
				nofollow={entry.data.seoMetadata?.nofollow ?? false}
				noindex={Boolean(entry?.data?.seoMetadata?.noindex)}
			/>
			{children}
		</>
	)
}
